import useCoveoCommerceExtras from "@DSG/Components/Coveo/useCoveoCommerceExtras";
import colorPallete, { bodyFonts, labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import { getSettingsCollection } from "@DSG/Overrides/Store/Context/ContextSelectors";
import CoveoApplicationState from "@DSG/Store/Data/CoveoApplicationState/CoveoApplicationState";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { clearSearchHistory, getSearchHistory } from "@insite/client-framework/Services/AutocompleteService";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    focusedQuery?: string;
    closeAutocomplete: () => void;
    extendedStyles?: SearchHistoryStyles;
}

const mapStateToProps = (state: CoveoApplicationState) => ({
    searchHistoryEnabled: getSettingsCollection(state).searchSettings.searchHistoryEnabled,
    searchPath: getSettingsCollection(state).searchSettings.searchPath,
    coveoCommerce: state.DSGGlobalData,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface SearchHistoryStyles {
    wrapper?: InjectableCss;
    headingText?: TypographyPresentationProps;
    itemWrapper?: InjectableCss;
    itemLink?: LinkPresentationProps;
    clearAllButtonWrapper?: InjectableCss;
    clearAllButton?: InjectableCss;
}

export const searchHistoryStyles: SearchHistoryStyles ={
    wrapper: {
        css: css`
            padding: 0 24px;
        `
    },
    headingText: {
        variant: "h6",
        css: css`
            border-bottom: 1px solid ${colorPallete.grey300};
            padding-bottom: 8px;
            margin-bottom: 14px;
            font-size: ${bodyFonts.body2RegularLg.fontSize};
            line-height: ${bodyFonts.body2RegularLg.lineHeight};
        `
    },
    itemWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-bottom: 14px;  
        `
    },
    itemLink: {
        css: css`
            font: ${labelFonts.label1Regular.fontWeight} ${labelFonts.label1Regular.fontSize}/${labelFonts.label1Regular.lineHeight} ${labelFonts.label1Regular.fontFamily};
            text-transform: lowercase;
            color: ${colorPallete.grey800};
            &:hover {
                text-decoration: underline;
            }
        `
    },
    clearAllButtonWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            button {
                border-radius: 30px;
                padding: 0 18px;
                width: 100%;
                justify-content: center;
                min-height: 38px;
                max-width: 175px;
                font: ${labelFonts.label2SemiBold.fontWeight} ${labelFonts.label2SemiBold.fontSize}/${labelFonts.label2SemiBold.lineHeight} ${labelFonts.label2SemiBold.fontFamily};
                outline-offset: 1px;
            }
        `
    }
}

const styles = searchHistoryStyles;

const CoveoSearchHistory = ({
    searchHistoryEnabled,
    searchPath,
    focusedQuery,
    closeAutocomplete,
    coveoCommerce,
}: Props) => {
    const { doCoveoSearch } = useCoveoCommerceExtras(coveoCommerce);

    const History = getSearchHistory().slice(0, 3);

    if (!searchHistoryEnabled) {
        return null;
    }

    const clearLinkClickHandler = () => {
        clearSearchHistory();
        closeAutocomplete();
    };

    if (History.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} className="searchHistory">
            <Typography {...styles.headingText}>{translate("Recent Searches")}</Typography>
            {History.map(search => {
                return (
                    <StyledWrapper {...styles.itemWrapper} key={search.query}>
                        <Link
                            {...styles.itemLink}
                            onClick={() => {
                                doCoveoSearch(search.query);
                                closeAutocomplete();
                            }}
                        >
                            {search.query}
                        </Link>
                    </StyledWrapper>
                )
            })}
            <StyledWrapper {...styles.clearAllButtonWrapper}>
                <Link {...styles.clearAllButton} className="dsgSecondaryDarkBtn" onClick={clearLinkClickHandler}>
                    {translate("Clear all recent searches")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
}

export default connect(mapStateToProps)(CoveoSearchHistory);